/**
 * @file ProblemDescriptionPanel.jsx
 * @description Left-side panel displaying problem description, sample test cases,
 *              constraints, topic/company tags, hints (via modal), and solution viewer.
 *              Supports both standalone DSA practice and test-mode contexts.
 *
 * @module components/ProblemDescriptionPanel
 */
import React, { useState, useRef, useEffect } from 'react';
import { FiPlay, FiX, FiCheckCircle, FiEye, FiBookOpen, FiCode, FiSettings, FiChevronDown, FiChevronRight, FiCheck, FiTag, FiBriefcase } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { FaLightbulb } from 'react-icons/fa';
import SolutionPanel from './SolutionPanel';

/**
 * Modal that displays a hint for the current problem.
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible.
 * @param {Function} props.onClose - Callback to close the modal.
 * @param {string} props.hint - The hint text to display.
 * @param {string} props.questionTitle - The title of the question.
 * @returns {JSX.Element|null}
 */
const HintModal = ({ isOpen, onClose, hint, questionTitle }) => {
    if (!isOpen) return null;
    return (
        <>
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50" onClick={onClose} />
            <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
                <div className="bg-accent border border-border shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden rounded">
                    <div className="flex items-start justify-between p-4 border-b border-border">
                        <div className="flex items-start justify-center space-x-1">
                            <div className="py-1.5"><FaLightbulb className="text-yellow-500 text-lg" /></div>
                            <div>
                                <h3 className="text-lg font-semibold text-foreground text-left">Hint</h3>
                                <p className="text-sm text-muted-foreground truncate max-w-md">{questionTitle}</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-2 text-muted-foreground hover:text-foreground rounded-lg transition-all duration-200"><FiX className="text-lg" /></button>
                    </div>
                    <div className="p-2 overflow-y-auto max-h-[60vh]">
                        {hint ? (
                            <div className="px-1.5 py-1">
                                <div className="flex items-center mb-3"><FiEye className="text-yellow-500 mr-2" /><span className="text-yellow-600 font-medium">Solution Approach</span></div>
                                <div className="text-foreground leading-relaxed whitespace-pre-wrap text-sm text-left">{hint}</div>
                            </div>
                        ) : (
                            <div className="text-center py-8"><FaLightbulb className="text-4xl text-gray-300 mx-auto mb-3" /><p className="text-muted-foreground">No hint available for this problem.</p></div>
                        )}
                    </div>
                    <div className="p-4 border-t border-border bg-gray-50">
                        <div className="flex justify-end"><button onClick={onClose} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded transition-colors">Got it!</button></div>
                    </div>
                </div>
            </div>
        </>
    );
};

/**
 * Renders a company logo tag from the local /company_logos/ directory.
 * Falls back to a text label if the logo image fails to load.
 *
 * @param {Object} props
 * @param {string} props.company - Company name to display/lookup.
 * @param {string} [props.className] - Optional additional CSS classes.
 * @param {boolean} [props.isGrayscale=false] - Whether to render the logo in grayscale.
 * @returns {JSX.Element|null}
 */
const CompanyTag = ({ company, className = "", isGrayscale = false }) => {
    const [hasError, setHasError] = useState(false);
    if (!company) return null;

    const normalizedName = company.toLowerCase().trim().replace(/\s+/g, '_');
    const logoPath = `/company_logos/${normalizedName}.png`;

    if (hasError) {
        return (
            <span className={`text-xs font-semibold ${isGrayscale ? 'text-muted-foreground opacity-50' : 'text-amber-700'} capitalize ${className}`}>
                {company}
            </span>
        );
    }

    return (
        <div className="flex items-center justify-center h-8 w-auto pr-2" title={company.charAt(0).toUpperCase() + company.slice(1)}>
            <img src={logoPath} alt={company} onError={() => setHasError(true)} className={`h-6 w-auto max-w-[70px] object-contain transition-all duration-300 ${isGrayscale ? 'grayscale opacity-70' : 'grayscale-0 opacity-100'}`} />
        </div>
    );
};

/**
 * The main panel that displays the problem description, sample test cases,
 * tags, companies, and hints.
 * 
 * @param {Object} props
 * @param {Object} props.question - The coding problem data.
 * @param {Array} props.sampleTestCases - Array of sample test cases to display.
 * @param {boolean} props.isAuthenticated - Whether the user is logged in.
 * @param {Function} props.navigate - Navigation function for routing.
 * @param {boolean} props.isTestMode - Whether the panel is being viewed during an active test (hides solutions/hints).
 * @param {boolean} props.isSolved - Whether the user has previously solved this problem.
 * @param {number} props.marks - The weight/marks of the problem (for tests).
 * @returns {JSX.Element}
 */
const ProblemDescriptionPanel = ({ question, sampleTestCases, isAuthenticated, navigate, isTestMode, isSolved, marks }) => {
    const [isHintModalOpen, setIsHintModalOpen] = useState(false);
    const [expandedTestCase, setExpandedTestCase] = useState(null);
    const [activeTab, setActiveTab] = useState('description');
    const [isTopicExpanded, setIsTopicExpanded] = useState(false);
    const [isCompanyExpanded, setIsCompanyExpanded] = useState(false);
    const [isSolutionExpanded, setIsSolutionExpanded] = useState(false);
    const [isConstraintsExpanded, setIsConstraintsExpanded] = useState(false);
    
    // Refs for auto-scroll
    const constraintsRef = useRef(null);
    const topicsRef = useRef(null);
    const companyRef = useRef(null);
    const solutionRef = useRef(null);
    const testCaseRefs = useRef({});

    const scrollToSection = (ref) => {
        if (ref && ref.current) {
            setTimeout(() => {
                ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 100);
        }
    };

    useEffect(() => {
        if (isConstraintsExpanded) scrollToSection(constraintsRef);
    }, [isConstraintsExpanded]);

    useEffect(() => {
        if (isTopicExpanded) scrollToSection(topicsRef);
    }, [isTopicExpanded]);

    useEffect(() => {
        if (isCompanyExpanded) scrollToSection(companyRef);
    }, [isCompanyExpanded]);

    useEffect(() => {
        if (isSolutionExpanded) scrollToSection(solutionRef);
    }, [isSolutionExpanded]);

    useEffect(() => {
        if (expandedTestCase !== null && testCaseRefs.current[expandedTestCase]) {
            setTimeout(() => {
                testCaseRefs.current[expandedTestCase].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 100);
        }
    }, [expandedTestCase]);

    const getDifficultyColor = (difficulty) => {
        switch (difficulty?.toLowerCase()) {
            case 'easy': return 'text-green-700 ';
            case 'medium': return 'text-yellow-700 ';
            case 'hard': return 'text-red-700 ';
            default: return 'text-muted-foreground ';
        }
    };

    if (!question) {
        return (
            <div className="h-full flex items-center justify-center">
                <div className="text-center"><FiBookOpen className="text-4xl text-gray-300 mx-auto mb-3" /><p className="text-muted-foreground">Loading problem description...</p></div>
            </div>
        );
    }

    return (
        <div className="h-full overflow-y-auto bg-accent text-foreground">
            <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
            <HintModal isOpen={isHintModalOpen} onClose={() => setIsHintModalOpen(false)} hint={question.hint} questionTitle={question.title} />
            <div className="p-4 space-y-6">
                <div className="space-y-4">
                    <div className="mb-2">
                        <div className="md:flex block items-start justify-between gap-4">
                            <div className="flex items-start space-x-3 flex-1 min-w-0">
                                {!isTestMode && <span className="text-emerald-600 text-md md:text-lg font-bold pl-1 flex-shrink-0">#{question.id}</span>}
                                <h1 className="flex items-center justify-start text-md md:text-xl font-bold text-foreground text-left flex-1">
                                    {question.title}
                                    {(isSolved || question.is_solved) && (
                                        <FiCheck className="ml-2 bg-emerald-600 text-white p-0.5 rounded-full w-5 h-5 self-center" title="Solved" />
                                    )}
                                    {!isTestMode && (
                                        <span className={`ml-auto px-1 py-0.5 font-medium text-xs ${getDifficultyColor(question.difficulty)}`}>
                                            {question.difficulty?.charAt(0)?.toUpperCase() + question.difficulty?.slice(1)}
                                        </span>
                                    )}
                                    {marks !== undefined && (
                                        <span className="ml-auto text-sm text-emerald-600 font-bold whitespace-nowrap self-center drop-shadow-md">
                                            {marks} {marks === 1 ? 'Mark' : 'Marks'}
                                        </span>
                                    )}
                                </h1>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="space-y-6">
                        {question.description && (
                            <div className="bg-accent">
                                <div className="py-2 px-1">
                                    <div className="text-foreground leading-relaxed whitespace-pre-wrap text-justify text-sm">{question.description}<br></br></div>
                                </div>
                                {question.pre_explanation && (
                                    <div className="pr-3 mt-4 pl-1"><div className="text-left text-muted-foreground text-sm mb-2 whitespace-pre-wrap leading-relaxed space-y-1">{question.pre_explanation}</div></div>
                                )}
                                <div className="block items-start justify-start gap-40 text-sm mt-4 pr-2 mb-2 pl-1">
                                    {question.input_image_path && (
                                        <div className="flex flex-col items-start">
                                            <img src={`/${question.input_image_path}`} alt="Input visualization" className="w-auto" style={{ height: 'auto', maxHeight: '15rem', minHeight: '4rem' }} onLoad={(e) => { const img = e.target; const aspectRatio = img.naturalWidth / img.naturalHeight; if (aspectRatio > 4) { img.style.height = '4rem'; } else if (aspectRatio > 2.5) { img.style.height = '9rem'; } else { img.style.height = '12rem'; } }} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                                            <div className="hidden items-center justify-center h-24 text-muted-foreground text-sm">Image not available</div>
                                        </div>
                                    )}
                                    {question.output_image_path && (
                                        <div className="flex flex-col items-start mt-4 pl-1">
                                            <img src={`/${question.output_image_path}`} alt="Output visualization" className="w-auto" style={{ height: 'auto', maxHeight: '12rem', minHeight: '4rem' }} onLoad={(e) => { const img = e.target; const aspectRatio = img.naturalWidth / img.naturalHeight; if (aspectRatio > 4) { img.style.height = '4rem'; } else if (aspectRatio > 2.5) { img.style.height = '9rem'; } else { img.style.height = '12rem'; } }} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                                            <div className="hidden items-center justify-center h-24 text-muted-foreground text-sm">Image not available</div>
                                        </div>
                                    )}
                                </div>
                                {question.post_explanation && (
                                    <div className="pr-3 mt-4 pl-1"><div className="text-left text-muted-foreground text-sm mb-2 whitespace-pre-wrap leading-relaxed space-y-1">{question.post_explanation}</div></div>
                                )}
                            </div>
                        )}

                        <div className="bg-accent">
                            <div className="py-2 px-1 border-b border-gray-300">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1">
                                        <FiPlay className="text-md font-semibold text-emerald-600" />
                                        <h3 className="text-md font-semibold text-emerald-600">Sample Test Cases</h3>
                                    </div>
                                </div>
                            </div>
                            <div className="divide-y divide-gray-300 border-b border-gray-300">
                                {sampleTestCases && sampleTestCases.length > 0 ? (
                                    sampleTestCases.map((testCase, index) => (
                                        <div 
                                            key={index} 
                                            ref={el => testCaseRefs.current[index] = el}
                                            className="py-2 px-1 hover:bg-emerald-50/50 transition-colors duration-200"
                                        >
                                            <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpandedTestCase(expandedTestCase === index ? null : index)}>
                                                <div className="flex items-center space-x-3">
                                                    <span className="font-medium text-foreground text-sm w-[72px] shrink-0">Example {index + 1}</span>
                                                    {expandedTestCase !== index && (
                                                        <div className="flex items-center space-x-3 text-sm text-muted-foreground">
                                                            <span className="truncate max-w-[100px]">Input: {testCase.input ? `${testCase.input.substring(0, 20)}${testCase.input.length > 20 ? '...' : ''}` : 'N/A'}</span>
                                                            <span>→</span>
                                                            <span className="truncate max-w-[100px]">{testCase.expected_output ? `${testCase.expected_output.substring(0, 20)}${testCase.expected_output.length > 20 ? '...' : ''}` : 'N/A'}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="text-muted-foreground mr-2">{expandedTestCase === index ? <FiChevronDown className="w-4 h-4" /> : <FiChevronRight className="w-4 h-4" />}</div>
                                            </div>
                                            <AnimatePresence>
                                                {expandedTestCase === index && (
                                                    <motion.div
                                                        initial={{ height: 0, opacity: 0 }}
                                                        animate={{ height: 'auto', opacity: 1 }}
                                                        exit={{ height: 0, opacity: 0 }}
                                                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                                                        className="overflow-hidden"
                                                    >
                                                        <div className="mt-1 space-y-2 pb-2">
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-1">
                                                                <div>
                                                                    <div className="flex items-center space-x-2 mb-2"><span className="text-sm font-medium text-muted-foreground">Input</span></div>
                                                                    <div className="py-1 text-sm text-green-600 flex items-start justify-start text-left truncate">{testCase.input}</div>
                                                                </div>
                                                                <div>
                                                                    <div className="flex items-center space-x-2 mb-2"><span className="text-sm font-medium text-muted-foreground">Expected Output</span></div>
                                                                    <div className="py-1 text-sm text-blue-600 whitespace-pre-wrap flex items-start justify-start text-left">{testCase.expected_output}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    ))
                                ) : (
                                    <div className="p-8 text-center text-muted-foreground">
                                        <FiPlay className="text-4xl mx-auto mb-3 opacity-50" />
                                        <p className="font-medium">No additional test cases available</p>
                                        <p className="text-sm mt-1 text-muted-foreground">More test cases will be used during evaluation</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {question.constraints && (
                            <div className="bg-accent" ref={constraintsRef}>
                                <div 
                                    className="py-2 px-1 border-b border-gray-300 flex items-center justify-between cursor-pointer hover:bg-emerald-50/50 transition-colors"
                                    onClick={() => setIsConstraintsExpanded(!isConstraintsExpanded)}
                                >
                                    <div className="flex items-center gap-1">
                                        <FiSettings className="text-md font-semibold text-emerald-600" />
                                        <h3 className="text-md font-semibold text-emerald-600">Constraints</h3>
                                    </div>
                                    <div className="text-muted-foreground mr-2">
                                        {isConstraintsExpanded ? <FiChevronDown className="w-4 h-4" /> : <FiChevronRight className="w-4 h-4" />}
                                    </div>
                                </div>
                                <AnimatePresence>
                                    {isConstraintsExpanded && (
                                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                            <div className="p-1 mt-2 mb-2">
                                                <div className="text-muted-foreground leading-relaxed text-left text-sm space-y-1">
                                                    {question.constraints.split(',').map((constraint, index) => (
                                                        <div key={index} className="flex items-start space-x-2"><span className="text-emerald-600 mt-0.5">•</span><span className="flex-1">{constraint.trim()}</span></div>
                                                    ))}
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        )}

                        {/* Topic Tags */}
                        {!isTestMode && question.topic && (
                            <div className="bg-accent" ref={topicsRef}>
                                <div 
                                    className="py-2 px-1 border-b border-gray-300 flex items-center justify-between cursor-pointer hover:bg-emerald-50/50 transition-colors"
                                    onClick={() => setIsTopicExpanded(!isTopicExpanded)}
                                >
                                    <div className="flex items-center gap-1">
                                        <FiTag className="text-md font-semibold text-emerald-600" />
                                        <h3 className="text-md font-semibold text-emerald-600">Topic Tags</h3>
                                    </div>
                                    <div className="text-muted-foreground mr-2">
                                        {isTopicExpanded ? <FiChevronDown className="w-4 h-4" /> : <FiChevronRight className="w-4 h-4" />}
                                    </div>
                                </div>
                                <AnimatePresence>
                                    {isTopicExpanded && (
                                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                            <div className="py-2 px-1 flex flex-wrap gap-2">
                                                {question.topic.split(',').map((topic, idx) => (
                                                    <span key={idx} className="bg-emerald-600 text-white text-[12px] px-2 py-0.5 rounded">
                                                        {topic.trim()}
                                                    </span>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        )}

                        {/* Company Tags */}
                        {!isTestMode && isAuthenticated && (
                            <div className="bg-accent" ref={companyRef}>
                                <div 
                                    className="py-2 px-1 border-b border-gray-300 flex items-center justify-between cursor-pointer hover:bg-emerald-50/50 transition-colors"
                                    onClick={() => setIsCompanyExpanded(!isCompanyExpanded)}
                                >
                                    <div className="flex items-center gap-1">
                                        <FiBriefcase className="text-md font-semibold text-emerald-600" />
                                        <h3 className="text-md font-semibold text-emerald-600">Company Tags</h3>
                                    </div>
                                    <div className="text-muted-foreground mr-2">
                                        {isCompanyExpanded ? <FiChevronDown className="w-4 h-4" /> : <FiChevronRight className="w-4 h-4" />}
                                    </div>
                                </div>
                                <AnimatePresence>
                                    {isCompanyExpanded && (
                                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                            <div className="px-1 py-2 flex flex-wrap items-center gap-3">
                                                {question.company_tag ? (
                                                    question.company_tag.split(',')
                                                        .map(c => c.trim())
                                                        .filter(c => c !== "")
                                                        .sort((a, b) => a.localeCompare(b))
                                                        .map((company, idx) => (
                                                            <div key={idx}>
                                                                <CompanyTag company={company} />
                                                            </div>
                                                        ))
                                                ) : (
                                                    <div className="text-sm text-muted-foreground">No company tags available for this problem.</div>
                                                )}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        )}

                        {/* Solution Section */}
                        {!isTestMode && isAuthenticated && (
                            <div className="hidden md:block bg-accent" ref={solutionRef}>
                                <div 
                                    className="py-2 px-1 border-b border-gray-300 flex items-center justify-between cursor-pointer hover:bg-emerald-50/50 transition-colors"
                                    onClick={() => setIsSolutionExpanded(!isSolutionExpanded)}
                                >
                                    <div className="flex items-center gap-1">
                                        <FiCode className="text-md font-semibold text-emerald-600" />
                                        <h3 className="text-md font-semibold text-emerald-600">Solution</h3>
                                    </div>
                                    <div className="text-muted-foreground mr-2">
                                        {isSolutionExpanded ? <FiChevronDown className="w-4 h-4" /> : <FiChevronRight className="w-4 h-4" />}
                                    </div>
                                </div>
                                <AnimatePresence>
                                    {isSolutionExpanded && (
                                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                                            <div className="relative overflow-hidden w-full" style={{ height: '500px' }}>
                                                <SolutionPanel questionId={question.id} isAuthenticated={isAuthenticated} navigate={navigate} />
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        )}
                    </div>
            </div>
        </div>
    );
};

export default ProblemDescriptionPanel;